import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { change, formValueSelector } from "redux-form";
import ContactAddressInput from "./inputs/ContactAddressInput";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";
import {
  CONTACT_ADDRESS_ADDRESS1,
  CONTACT_ADDRESS_ADDRESS2,
  CONTACT_ADDRESS_CITY,
  CONTACT_ADDRESS_COUNTRY_CODE,
  CONTACT_ADDRESS_POSTAL_CODE,
  CONTACT_ADDRESS_REGION_CODE,
  CONTACT_LANGUAGE_CODE,
  CONTACT_TIMEZONE,
} from "src/shared/contacts/forms/inputs/address/IAddressData";
import { useTimezones } from "src/shared/contacts/timezones/useTimezones";
import { LanguageInput } from "src/shared/forms/inputs/LanguageInput";
import { SelectInput } from "src/shared/forms/inputs/SelectInputOld";
import TimezoneInput from "src/shared/forms/inputs/TimezoneInput";
import { useLanguages } from "src/shared/languages/useLanguages";

export interface IContactAddressFormProps {
  form: string;
  countriesDisabled?: boolean;
}

export default (props: IContactAddressFormProps) => {
  const { form, countriesDisabled } = props;

  const selector = formValueSelector(form);
  const countryCode = useSelector((state) =>
    selector(state, CONTACT_ADDRESS_COUNTRY_CODE),
  );
  const regionCode = useSelector((state) =>
    selector(state, CONTACT_ADDRESS_REGION_CODE),
  );
  const timezone = useSelector((state) => selector(state, CONTACT_TIMEZONE));
  const languageCode = useSelector((state) =>
    selector(state, CONTACT_LANGUAGE_CODE),
  );

  const dispatch: ThunkDispatchType = useDispatch();
  const languagesResult = useLanguages(countryCode, {
    enabled: Boolean(countryCode),
  });
  const { data: languages } = languagesResult;
  const timezonesResult = useTimezones(countryCode ?? "", regionCode ?? "", {
    enabled: Boolean(countryCode && regionCode),
  });
  const { data: timezones } = timezonesResult;

  // Clear language selection and reload language list
  useEffect(() => {
    if (
      languages &&
      !languages.some((language) => language.languageCode === languageCode)
    ) {
      dispatch(change(form, CONTACT_LANGUAGE_CODE, null));
    }
  }, [dispatch, form, languageCode, languages]);

  // Clear timezone selection and reload timezone list
  useEffect(() => {
    if (timezones && !timezones.some((tz) => tz.timezone === timezone)) {
      dispatch(change(form, CONTACT_TIMEZONE, null));
    }
  }, [dispatch, form, timezone, timezones]);

  return (
    <>
      <ContactAddressInput
        form={form}
        address1InputID={CONTACT_ADDRESS_ADDRESS1}
        address2InputID={CONTACT_ADDRESS_ADDRESS2}
        cityInputID={CONTACT_ADDRESS_CITY}
        regionCodeInputID={CONTACT_ADDRESS_REGION_CODE}
        countryCodeInputID={CONTACT_ADDRESS_COUNTRY_CODE}
        postalCodeInputID={CONTACT_ADDRESS_POSTAL_CODE}
        countriesDisabled={countriesDisabled}
      />

      <Row>
        <Col sm={6}>
          {!countryCode || !regionCode ? (
            <TimezoneInput inputID={CONTACT_TIMEZONE} disabled timezones={[]} />
          ) : (
            <ReactQueryResult {...timezonesResult}>
              {(timezones) => (
                <TimezoneInput
                  inputID={CONTACT_TIMEZONE}
                  timezones={timezones}
                />
              )}
            </ReactQueryResult>
          )}
        </Col>
        <Col sm={6}>
          {!countryCode ? (
            <LanguageInput
              inputID={CONTACT_LANGUAGE_CODE}
              languages={[]}
              disabled
              SelectComponent={SelectInput}
              isRequired
            />
          ) : (
            <ReactQueryResult {...languagesResult}>
              {(languages) => (
                <LanguageInput
                  inputID={CONTACT_LANGUAGE_CODE}
                  languages={languages}
                  SelectComponent={SelectInput}
                  isRequired
                />
              )}
            </ReactQueryResult>
          )}
        </Col>
      </Row>
    </>
  );
};
